import React, {
  forwardRef,
  MouseEventHandler,
  ReactNode,
  Ref,
  useCallback,
  useRef
} from 'react';
import {
  ChildrenProp,
  ClassNameProp,
  HBox,
  Icon,
  MenuTool,
  ValidIconName,
  VBox
} from '..';
import { useAccessibleElementProps, useSingleAndDoubleClick } from '../../lib';
import { isEmptyArray, mergeRefs } from '../../util';
import { icModel } from 'generated-ts-common';

type ID = icModel.saf.infrastructure.model.common.ID;

/**
 * Props for a {@link Document} component.
 * @author agd
 */
export interface DocumentProps extends ClassNameProp, ChildrenProp {
  /**
   * The title line of the Document.
   * This is not necessarily the document's actual title, but rather what will be displayed to the user.
   */
  title: string;
  /**
   * Text which will be displayed below the document's title.
   */
  content?: string;
  /**
   * Icon of the Document.
   */
  icon?: ValidIconName;
  /**
   * All the {@link Tool}s that should be used for the Document.
   * If there are more tools than the defined threshold, all the tools
   * that are not set as static (in `staticTools`) will be wrapped in a {@link MenuTool}.
   *
   * If one of these tools is already a {@link MenuTool}, make sure it is either static or
   * that the overflowThreshold is not exceeded. Otherwise, the wrapping of a {@link MenuTool}
   * inside of another {@link MenuTool} will most likely throw an exception.
   */
  tools?: JSX.Element | JSX.Element[];
  /**
   * Indexes of the tools that should be considered static.
   */
  staticTools?: number[];
  /**
   * The threshold for when tools start to be wrapped in a {@link MenuTool}.
   * This number is inclusive, so if set to 3, the tools will be shown as menu at 4 tools.
   */
  overflowThreshold?: number;
  /**
   * Handler for when the Document component is clicked.
   */
  onClick?: MouseEventHandler;
  /**
   * Handler for when the Document component is double clicked.
   */
  onDoubleClick?: MouseEventHandler;
  /**
   * If the document is deprecated.
   */
  deprecated?: boolean;
  /**
   * Optional selection state of the Document.
   */
  selected?: {
    /**
     * Class to give to the selected document.
     */
    class: 'primary' | 'secondary' | string;
  };
  /**
   * If the document has been read or not.
   */
  documentRead?: boolean;
  /**
   * Optional if we want to display additionalItem
   * Item will be shown in document before {@link MenuTool}
   */
  additionalItem?: ReactNode | null;
  /**
   * a unique identifier for the displayed document.
   */
  unqiueDocID?: ID;
}

export function docIdToClassName(id?: ID): string | undefined {
  if (!id) {
    return undefined;
  }
  const serializedID = JSON.stringify(id);
  return 'docId_' + serializedID.replace(/["{}]/g, '').replace(/\W/g, '_');
}

/**
 * Component used to represent a Document.
 *
 * @param props a {@link DocumentProps} object
 * @constructor
 * @author agd
 * @return the created {@link JSX.Element}
 */
export const Document = forwardRef(function Document(
  props: DocumentProps,
  ref: Ref<HTMLDivElement>
): JSX.Element {
  // use custom hook for handling single click and double click
  const [clickHandler, dblClickHandler] = useSingleAndDoubleClick(
    props.onClick,
    props.onDoubleClick
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const getContainer = useCallback(() => containerRef?.current, [containerRef]);

  /**
   * Retrieves css string for the given docs of the Document.
   * @return css string
   */
  const getDocCss = (): string => {
    const css = ['ehs-document'];
    if (props.onClick || props.onDoubleClick) {
      css.push('has-click-action');
    }

    // selected documents have a specific kind of highlighting
    if (props.selected) {
      css.push('selected');
      css.push(props.selected.class);
    }

    // deprecated documents have line-through text
    if (props.deprecated) {
      css.push('deprecated');
    }

    // if the document hasn't been read, give it a bold title
    // `=== false` because documentRead can be undefined, in which case it should be treated as 'read'
    if (props.documentRead === false) {
      css.push('not-read');
    }

    // custom classes
    if (props.className) {
      css.push(props.className);
    }

    return css.join(' ');
  };

  /**
   * Retrieves the tools for the given docs of the Document.
   * @return the list of {@link React.ReactNode} corresponding to the given props.
   */
  const getToolComponents = (): React.ReactNode => {
    if (!props.tools) {
      return [];
    }

    // define threshold
    const threshold = props.overflowThreshold ? props.overflowThreshold : 3;

    let tools: React.ReactNode;
    // transform the props.tools to JSX.Element[]
    let passedTools: JSX.Element[];
    if (Array.isArray(props.tools)) {
      passedTools = props.tools;
    } else {
      passedTools = [props.tools];
    }

    // handle MenuTool threshold
    if (passedTools.length > threshold) {
      // static and non-static tools that will be rendered accordingly
      const staticTools: React.ReactNode[] = [];
      const nonStaticTools: React.ReactNode[] = [];
      // indexes of the tools that are meant to be static
      const staticToolIndexes: number[] = props.staticTools ?? [];

      // staticToolIndexes handling
      if (!isEmptyArray(staticToolIndexes)) {
        passedTools.forEach((tool, index) => {
          if (staticToolIndexes.indexOf(index) >= 0) {
            staticTools.push(tool);
          } else {
            nonStaticTools.push(tool);
          }
        });
      } else {
        // simply use the passedTools
        nonStaticTools.push(...passedTools);
      }

      // build the tools that will be returned
      tools = (
        <>
          {staticTools}
          <MenuTool containerElement={getContainer}>{nonStaticTools}</MenuTool>
        </>
      );
    } else {
      // return the tools as array
      tools = passedTools;
    }

    return tools;
  };

  // the tools of the component
  const toolComponents = getToolComponents();

  const importProgressCls =
    'icon-area-overlay' + props.unqiueDocID
      ? ' ' + docIdToClassName(props.unqiueDocID)
      : '';

  // Document content elements
  const docContentElements = (
    <>
      {props.icon && (
        <div className={'icon-area'}>
          <div className={importProgressCls} />
          <Icon
            aria-label={undefined}
            iconName={props.icon}
            className="doc-icon"
          />
        </div>
      )}

      <div className="detail">
        <div className="title">{props.title}</div>
        {props.content ? <div className="content">{props.content}</div> : <></>}
      </div>
      {props.additionalItem}
      {toolComponents}
    </>
  );

  const accessibleProps = useAccessibleElementProps({
    className: getDocCss(),
    onClick: props.onClick ? clickHandler : undefined
  });

  // props to add to the top-level container of the created element
  const containerProps = {
    ...accessibleProps,
    onDoubleClick: props.onDoubleClick ? dblClickHandler : undefined,
    ref
  };

  return (
    <>
      {props.children ? (
        <VBox {...containerProps}>
          <HBox className="document-container" ref={containerRef}>
            {docContentElements}
          </HBox>
          <VBox className="document-children">{props.children}</VBox>
        </VBox>
      ) : (
        <HBox
          {...containerProps}
          ref={mergeRefs([containerProps.ref, containerRef])}
        >
          {docContentElements}
        </HBox>
      )}
    </>
  );
});
